import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { api } from "network/network";
import { APIendpoints } from "utils/constants/APIendpoints";
import apiEndpoint, { API_HOST_WMS, API_HOST_HP } from "config/api.config";
import { LS_KEYS } from "utils/constants/localStorageKeys";
import _get from "lodash-es/get";
import {
  MINUTES_IN_HOUR,
  MS_IN_MINUTE,
} from "./constants.bids";

const HP_BASE_URL = (apiEndpoint[API_HOST_HP] || "").replace(/\/$/, "");
const BIDDING_HOST = API_HOST_WMS;
/* ─── Helpers ──────────────────────────────────────────────────── */

const formatDate = (rfc) => {
  if (!rfc) return "";
  const d = new Date(rfc);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const formatCountdown = (targetDate) => {
  const diffMs = targetDate - Date.now();
  if (diffMs <= 0) return null;
  const totalMins = Math.floor(diffMs / MS_IN_MINUTE);
  const hours = Math.floor(totalMins / MINUTES_IN_HOUR);
  const mins = totalMins % MINUTES_IN_HOUR;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const formatTime = (date) => {
  const options = date.getMinutes() === 0
    ? { hour: "numeric" }
    : { hour: "numeric", minute: "2-digit" };

  return date.toLocaleTimeString("en-IN", options).replace(/\s?(am|pm)$/i, (match) => ` ${match.trim().toUpperCase()}`);
};

const formatTimeLabel = (session, uiStatus) => {
  const end = session.end_time ? new Date(session.end_time) : null;
  const start = session.start_time ? new Date(session.start_time) : null;
  if (uiStatus === "upcoming" && start) {
    return `Starts on ${start.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}, ${formatTime(start)}`;
  }
  if (end) {
    if (["live", "submitted"].includes(uiStatus)) {
      const countdown = formatCountdown(end);
      if (countdown) return `Ends in ${countdown}`;
      return `Ends by ${end.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}, ${formatTime(end)}`;
    }
    return `Ended on ${end.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}, ${formatTime(end)}`;
  }
  return "";
};

const formatDeliveryRange = (start, end) => {
  if (!start && !end) return "—";
  const s = start ? new Date(start) : null;
  const e = end ? new Date(end) : null;
  const fmt = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }).replace(" ", "");
  if (s && e) return `${fmt(s)}-${fmt(e)}`;
  if (s) return `From ${fmt(s)}`;
  if (e) return `Till ${fmt(e)}`;
};

const getItemUnit = (item = {}) => item.unit_of_measurement || item.sub_uom || item.uom || "kg";
const getTaxPercentage = (item = {}) => Number(item.tax_percentage ?? item.tax_rate ?? 0) || 0;

const formatCompactAmount = (amount) => {
  const value = Number(String(amount ?? "").replace(/,/g, ""));
  if (!Number.isFinite(value)) return "—";

  const format = (num) => Number(num.toFixed(1)).toString();

  if (Math.abs(value) >= 10000000) return `₹${format(value / 10000000)}Cr`;
  if (Math.abs(value) >= 100000) return `₹${format(value / 100000)}L`;
  if (Math.abs(value) >= 1000) return `₹${format(value / 1000)}K`;
  return `₹${format(value)}`;
};

const toCount = (count) => {
  const parsed = Number(count);

  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeOpportunityWorth = (opportunityWorth = {}) => ({
  live: toCount(opportunityWorth.live),
  upcoming: toCount(opportunityWorth.upcoming),
  total: toCount(opportunityWorth.total),
});

const normalizeSessionCounts = (counts = {}) => ({
  awarded: toCount(counts.awarded ?? counts.awarded_count),
  live: toCount(counts.live ?? counts.live_count),
  upcoming: toCount(counts.upcoming ?? counts.upcoming_count),
  previous: toCount(counts.previous ?? counts.previous_count),
  with_submitted_bids: toCount(
    counts.with_submitted_bids ?? counts.withSubmittedBids ?? counts.submitted ?? counts.submitted_count
  ),
  net_worth_opportunity: normalizeOpportunityWorth(counts.net_worth_opportunity),
});

const mapToUIStatus = (biddingSession, sessionDetail = {}) => {
  const { bidding_status = "", allocation_status = "", status = "" } = biddingSession;
  const bs = bidding_status.toUpperCase();
  const as_ = allocation_status.toUpperCase();
  const st = status.toUpperCase();
  const userStatus = (sessionDetail.user_status || "").toUpperCase();
  const isApproved = st === "APPROVED";
  const isPreLiveBiddingStatus = ["CREATED", "UPCOMING"].includes(bs);

  if ((as_ === "AWARDED" || as_ === "SUCCESS") && userStatus === "AWARDED") return "awarded";
  if (as_ === "AWARDED" || as_ === "SUCCESS") return "completed";
  if (bs === "SUBMITTED" || bs === "IN_EVALUATION") return "submitted";
  if (bs === "NOT_AWARDED" || as_ === "NOT_AWARDED") return "not_awarded";
  if (isApproved && isPreLiveBiddingStatus) return "upcoming";
  if (st === "ACTIVE" || bs === "LIVE" || bs === "ACTIVE") return "live";
  if (st === "ENDED" || st === "CLOSED" || st === "COMPLETED") return "no_submission";
  if (bs === "COMPLETED") return "completed";
  if (isPreLiveBiddingStatus || st === "UPCOMING" || st === "CREATED") return "hidden";
  return "live";
};

const getUserStatusOverride = (sessionDetail = {}) => {
  const userStatus = (sessionDetail.user_status || "").toUpperCase();

  if (userStatus === "SUBMITTED") return "submitted";
  if (userStatus === "NOT_AWARDED") return "not_awarded";

  return null;
};

const isPreviousBiddingSession = (session) => {
  const biddingStatus = (session.rawSession?.bidding_status || "").toUpperCase();

  if (session.status !== "awarded") return biddingStatus === "COMPLETED";

  return ["COMPLETED", "IN_EVALUATION"].includes(biddingStatus);
};

const STATUS_TO_API_STATUS = {
  live:      "LIVE",
  upcoming:  "UPCOMING",
  awarded:   "COMPLETED",
  submitted: "LIVE",
  previous:  "COMPLETED",
};

const UI_STATUS_TO_API_STATUSES = {
  upcoming: ["UPCOMING", "CREATED"],
};

const getStringValue = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);

const transformSession = (sessionDetail, overrideStatus) => {
  const { bidding_session = {}, items = [], configs = [], properties = [] } = sessionDetail;

  const location =
    getStringValue(sessionDetail.location_city) ||
    getStringValue(bidding_session.location_city) ||
    getStringValue(properties.find((p) => p.name?.toLowerCase().includes("city") || p.name?.toLowerCase() === "location")?.value) ||
    getStringValue(configs.find((c) => c.key?.toLowerCase().includes("city") || c.key?.toLowerCase().includes("location"))?.value) ||
    getStringValue(properties.find((p) => p.name === "warehouse_code" || p.name === "outlet")?.value) ||
    "—";

  const totalAmountConfig =
    configs.find((c) => c.key === "total_amount" || c.key === "expected_value");
  const computedTotal = items.reduce((sum, item) => {
    return sum + (item.required_quantity || 0) * (item.expected_price || 0);
  }, 0);
  const totalAmountValue = totalAmountConfig?.value ?? computedTotal;
  const totalAmount = Number(totalAmountValue) > 0 ? formatCompactAmount(totalAmountValue) : "—";

  const uiStatus = overrideStatus || mapToUIStatus(bidding_session, sessionDetail);

  const products = items.slice(0, 8).map((item) => {
    const unit = getItemUnit(item);

    return {
      id: item.id,
      imageUrl: item.image_url || item.icon_url || item.imageUrl || null,
      name: item.name || `Item ${item.item_id}`,
      qty: item.required_quantity != null ? `${item.required_quantity}${unit}` : "",
      minQty: item.min_award_quantity != null ? `${item.min_award_quantity}${unit}` : "",
      totalQty: item.required_quantity != null ? `${item.required_quantity}${unit}` : "",
      price: item.expected_price ? `₹${item.expected_price}/${unit}` : "",
      bidding_session_item_id: item.id,
      unit,
      taxRate: getTaxPercentage(item),
    };
  });

  return {
    id: String(bidding_session.id),
    bidId: `AV${bidding_session.id}`,
    title: bidding_session.name || "Bid",
    location,
    deliveryRange: formatDeliveryRange(bidding_session.start_time, bidding_session.end_time),
    totalAmount,
    products,
    productCount: items.length,
    status: uiStatus,
    timeLabel: formatTimeLabel(bidding_session, uiStatus),
    startTime: bidding_session.start_time,
    endTime: bidding_session.end_time,
    creatorName:
      getStringValue(sessionDetail.creator_name) ||
      getStringValue(bidding_session.creator_name) ||
      "",
    creatorPhoneNumber:
      getStringValue(sessionDetail.creator_phone_number) ||
      getStringValue(bidding_session.creator_phone_number) ||
      "",
    address:
      getStringValue(sessionDetail.address) ||
      getStringValue(bidding_session.address) ||
      getStringValue(properties.find((p) => p.name?.toLowerCase().includes("address"))?.value) ||
      "",
    description:
      getStringValue(sessionDetail.description) ||
      getStringValue(bidding_session.description) ||
      getStringValue(configs.find((c) => c.key?.toLowerCase().includes("description") || c.key?.toLowerCase().includes("terms"))?.value) ||
      getStringValue(properties.find((p) => p.name?.toLowerCase().includes("description") || p.name?.toLowerCase().includes("terms"))?.value) ||
      "",
    isEditable: sessionDetail.is_editable,
    rawDetail: sessionDetail,
    rawSession: bidding_session,
    rawItems: items,
    rawConfigs: configs,
    rawProperties: properties,
  };
};

const transformBidItems = (bidDetails = [], itemBidGroups = []) => {
  const safeBidDetails = Array.isArray(bidDetails) ? bidDetails : [];
  const safeItemBidGroups = Array.isArray(itemBidGroups) ? itemBidGroups : [];
  const allBidDetails = safeBidDetails.length
    ? safeBidDetails
    : safeItemBidGroups.flatMap((group) => group.bid_details || []);
  if (!allBidDetails.length) return { items: [], bidId: null };

  const taxByItemId = {};
  safeItemBidGroups.forEach((group) => {
    const sessionItem = group.session_item || {};
    if (sessionItem.item_id != null) taxByItemId[sessionItem.item_id] = getTaxPercentage(sessionItem);
    (group.bid_details || []).forEach((detail) => {
      (detail.bid_items || []).forEach((bidItem) => {
        if (bidItem.item_id != null) taxByItemId[bidItem.item_id] = getTaxPercentage(bidItem);
      });
    });
  });

  const latest = allBidDetails.reduce((a, b) =>
    (a.bid?.version || 0) > (b.bid?.version || 0) ? a : b
  );

  const latestItemsBySessionItemId = {};
  (latest.bid_items || []).forEach((bi) => {
    const key = bi.bidding_session_item_id;
    if (key == null) return;

    const itemTax = bi.item_id != null ? taxByItemId[bi.item_id] : null;
    const mappedItem = {
      bidding_session_item_id: key,
      quantity_offered: bi.quantity_offered,
      price_offered: bi.price_offered,
      id: bi.id,
      item_id: bi.item_id,
      taxRate: getTaxPercentage(bi) || itemTax || 0,
    };
    const existingItem = latestItemsBySessionItemId[key];
    if (!existingItem || Number(mappedItem.id || 0) >= Number(existingItem.id || 0)) {
      latestItemsBySessionItemId[key] = mappedItem;
    }
  });

  return {
    bidId: latest.bid?.id || null,
    items: Object.values(latestItemsBySessionItemId),
  };
};

/* ─── Store ────────────────────────────────────────────────────── */

let latestBiddingSessionsRequestId = 0

const useBiddingStore = create(
  devtools((set, get) => ({
    // Bidding sessions list
    biddingSessionsStatus: "",
    biddingSessions: [],
    biddingSessionsError: null,
    hasNextBiddingSessionsPage: false,

    // Single session detail
    currentSessionStatus: "",
    currentSession: null,
    currentSessionError: null,

    // Vendor's existing bids for current session
    vendorBidsStatus: "",
    vendorBids: [],
    vendorBidId: null,
    vendorBidsError: null,

    // Upsert / Submit
    upsertBidStatus: "",
    submitBidStatus: "",

    // Session counts for filter page
    sessionCounts: { live: 0, upcoming: 0, previous: 0, with_submitted_bids: 0 },
    sessionCountsStatus: "",

    // Terms config (pre-fetched on filter page)
    termsConfig: { showTerms: true, audioUrl: null },
    termsConfigStatus: "",

    // Participant registration
    biddingParticipantId: null,

    // Home page data
    biddingHomeData: { images: {}, counts: {} },
    biddingHomeStatus: "",

    /* ── Fetch home page data (banner images + counts) ── */
    getBiddingHomeData: async () => {
      set({ biddingHomeStatus: "BEGIN" });
      const vendorId = window.localStorage.getItem(LS_KEYS.OUTLET_ID);
      const pid = get().biddingParticipantId;

      api
        .get(BIDDING_HOST, APIendpoints.getBiddingHomePage, {
          query: {
            ...(vendorId ? { participant_id: vendorId, participant_type: "VENDOR" } : {}),
            ...(pid ? { bidding_participant_id: pid } : {}),
          },
        })
        .then(({ data }) => {
          const rawImages = _get(data, "response.images", {});
          const images = Object.fromEntries(
            Object.entries(rawImages).map(([k, v]) =>
              [k, v ? `${HP_BASE_URL}/${v.replace(/^\//, "")}` : v]
            )
          );
          set({
            biddingHomeData: { images, counts: _get(data, "response.counts", {}) },
            biddingHomeStatus: "DONE",
          });
        })
        .catch(() => set({ biddingHomeStatus: "FAIL" }));
    },

    /* ── Fetch counts for filter page ── */
    getSessionCounts: async () => {
      set({ sessionCountsStatus: "BEGIN" });
      const vendorId = window.localStorage.getItem(LS_KEYS.OUTLET_ID);
      const body = vendorId ? { participant_id: vendorId, participant_type: "VENDOR" } : {};

      api
        .post(BIDDING_HOST, APIendpoints.getBiddingSessionCounts, body)
        .then(({ data }) => {
          const counts = _get(data, "response.counts", _get(data, "response", {}));
          set({ sessionCounts: normalizeSessionCounts(counts), sessionCountsStatus: "DONE" });
        })
        .catch(() => {
          set({ sessionCountsStatus: "FAIL" });
        });
    },

    /* ── Fetch list of sessions by UI status tab ── */
    getBiddingSessions: async (uiStatus, pageNumber = 1, pageSize = 20) => {
      const requestId = ++latestBiddingSessionsRequestId

      set({
        biddingSessionsStatus: "BEGIN",
        biddingSessions: [],
        hasNextBiddingSessionsPage: false,
      })

      const apiStatus = STATUS_TO_API_STATUS[uiStatus] || "LIVE";
      const apiStatuses = UI_STATUS_TO_API_STATUSES[uiStatus] || [apiStatus];
      const vendorId = window.localStorage.getItem(LS_KEYS.OUTLET_ID);
      const pid = get().biddingParticipantId;

      const baseBody = {
        page_size: pageSize,
        ...(vendorId ? { participant_id: vendorId, participant_type: "VENDOR" } : {}),
        ...(pid ? { bidding_participant_id: pid } : {}),
      };

      const requestStatuses = uiStatus === "submitted"
          ? ["SUBMITTED"]
          : uiStatus === "awarded"
            ? ["AWARDED"]
            : apiStatuses;

      const buildRequests = (pNum) => {
        const body = { ...baseBody, page_number: pNum };
        return uiStatus === "all"
          ? [api.post(BIDDING_HOST, APIendpoints.getBiddingSessions, body)]
          : requestStatuses.map((status) =>
              api.post(BIDDING_HOST, APIendpoints.getBiddingSessions, { ...body, status })
            );
      };

      const extractAndFilter = (responses) => {
        const seen = new Set();
        const raw = responses.flatMap(({ data }) => _get(data, "response.bidding_sessions", []))
          .filter((sessionDetail) => {
            const id = _get(sessionDetail, "bidding_session.id");
            if (!id) return true;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });
        return raw
          .map((s) => transformSession(
            s,
            ["all", "live", "submitted", "previous"].includes(uiStatus) ? getUserStatusOverride(s) : null
          ))
          .filter((s) => {
            if (uiStatus === "all") return s.status !== "hidden";
            if (uiStatus === "awarded") return s.status === "awarded";
            if (uiStatus === "submitted") return s.status === "submitted" || s.status === "not_awarded";
            if (uiStatus === "previous") return isPreviousBiddingSession(s);
            return s.status === uiStatus;
          });
      };

      const currentRequests = buildRequests(pageNumber);
      const peekRequests = buildRequests(pageNumber + 1);

      const [currentResults, peekResults] = await Promise.all([
        Promise.allSettled(currentRequests),
        Promise.allSettled(peekRequests),
      ]);

      // Race-condition guard: discard if a newer request has started
      if (requestId !== latestBiddingSessionsRequestId) return null;

      const anyCurrentRejected = currentResults.some((r) => r.status === "rejected");
      if (anyCurrentRejected) {
        const firstRejected = currentResults.find((r) => r.status === "rejected");
        set({
          biddingSessionsStatus: "FAIL",
          biddingSessionsError: _get(firstRejected.reason, ["response", "data", "error", "message"], "Failed to load bids"),
        });
        return null;
      }

      const currentResponses = currentResults.map((r) => r.value);
      const sessions = extractAndFilter(currentResponses);

      // Determine hasNextPage from peek result — safe degradation if peek fails
      let hasNextPage = false;
      const allPeekFulfilled = peekResults.every((r) => r.status === "fulfilled");
      if (allPeekFulfilled) {
        const peekResponses = peekResults.map((r) => r.value);
        const peekSessions = extractAndFilter(peekResponses);
        hasNextPage = peekSessions.length > 0;
      }

      set({
        biddingSessions: sessions,
        hasNextBiddingSessionsPage: hasNextPage,
        biddingSessionsStatus: "DONE",
      });

      return { hasNextPage };
    },

    /* ── Fetch single session + vendor's bids ── */
    getBiddingSession: async (sessionId, overrideStatus) => {
      set({ currentSessionStatus: "BEGIN", currentSession: null });

      api
        .get(BIDDING_HOST, APIendpoints.getBiddingSession, {
          query: { bidding_session_id: sessionId },
        })
        .then(({ data }) => {
          const raw = _get(data, "response.bidding_session");
          if (!raw) { set({ currentSessionStatus: "FAIL" }); return; }
          const mappedStatus = mapToUIStatus(raw.bidding_session || {}, raw);
          const statusOverride = overrideStatus || (mappedStatus === "live" ? getUserStatusOverride(raw) : null);
          set({ currentSessionStatus: "DONE", currentSession: transformSession(raw, statusOverride) });
        })
        .catch((err) => {
          set({
            currentSessionStatus: "FAIL",
            currentSessionError: _get(err, "response.data.error.message", "Failed to load bid details"),
          });
        });
    },

    /* ── Fetch vendor's placed bids for a session ── */
    getVendorBids: async (sessionId) => {
      set({ vendorBidsStatus: "BEGIN", vendorBids: [] });
      const vendorId = window.localStorage.getItem(LS_KEYS.OUTLET_ID);

      const body = {
        bidding_session_ids: [parseInt(sessionId)],
        ...(vendorId ? { participant_ids: [vendorId], participant_type: "VENDOR" } : {}),
        page_number: 1,
        page_size: 50,
      };

      api
        .post(BIDDING_HOST, APIendpoints.getBids, body)
        .then(({ data }) => {
          const bidDetails = _get(data, "response.bid_details", []);
          const itemBidGroups = _get(data, "response.item_bid_groups", []);
          const { items, bidId } = transformBidItems(bidDetails, itemBidGroups);
          set({ vendorBidsStatus: "DONE", vendorBids: items, vendorBidId: bidId });
        })
        .catch((err) => {
          set({
            vendorBidsStatus: "FAIL",
            vendorBidsError: _get(err, "response.data.error.message", "Failed to load your bids"),
          });
        });
    },

    /* ── Submit / upsert bids ── */
    upsertBids: async (sessionId, bidItems, onSuccess, onError) => {
      set({ upsertBidStatus: "BEGIN" });
      const vendorId = window.localStorage.getItem(LS_KEYS.OUTLET_ID);

      const body = {
        bidding_session_id: parseInt(sessionId),
        participant_id: vendorId || "",
        participant_type: "VENDOR",
        bid_items: bidItems,
      };

      api
        .post(BIDDING_HOST, APIendpoints.upsertBids, body)
        .then(({ data }) => {
          set({ upsertBidStatus: "DONE" });
          onSuccess && onSuccess(_get(data, "response.bid_detail"));
        })
        .catch((err) => {
          set({ upsertBidStatus: "FAIL" });
          onError && onError(_get(err, "response.data.error.message", "Failed to submit bid"));
        });
    },

    /* ── Register as participant (call once before placing bid) ── */
    registerParticipant: async () => {
      const outletId = window.localStorage.getItem(LS_KEYS.OUTLET_ID);
      const outletName = window.localStorage.getItem(LS_KEYS.OUTLET_NAME);

      api
        .post(BIDDING_HOST, APIendpoints.registerBiddingParticipant, {
          participant_id: outletId || "",
          participant_name: outletName || "",
          participant_type: "SELLER",
        })
        .then(({ data }) => {
          const pid = _get(data, "response.bidding_participant_id");
          if (pid) set({ biddingParticipantId: pid });
        })
        .catch(() => {});
    },

    /* ── Pre-fetch terms config (called from filter page) ── */
    fetchBiddingTerms: async () => {
      set({ termsConfigStatus: "BEGIN" });
      const vendorId = window.localStorage.getItem(LS_KEYS.OUTLET_ID);
      api
        .get(BIDDING_HOST, APIendpoints.getBiddingTerms, {
          query: { ...(vendorId ? { participant_id: vendorId, participant_type: "VENDOR" } : {}) },
        })
        .then(({ data }) => {
          const config = {
            showTerms: _get(data, "response.show_terms", true),
            audioUrl: _get(data, "response.audio_url", null),
          };
          set({ termsConfig: config, termsConfigStatus: "DONE" });
        })
        .catch(() => {
          set({ termsConfigStatus: "FAIL" });
        });
    },

    /* ── Get terms for a specific session (overrides cached config) ── */
    getBiddingTerms: async (sessionId) => {
      const vendorId = window.localStorage.getItem(LS_KEYS.OUTLET_ID);
      return api
        .get(BIDDING_HOST, APIendpoints.getBiddingTerms, {
          query: {
            bidding_session_id: parseInt(sessionId),
            ...(vendorId ? { participant_id: vendorId, participant_type: "VENDOR" } : {}),
          },
        })
        .then(({ data }) => {
          const config = {
            showTerms: _get(data, "response.show_terms", true),
            audioUrl: _get(data, "response.audio_url", null),
          };
          set({ termsConfig: config });
          return config;
        })
        .catch(() => get().termsConfig);
    },

    /* ── Submit bids for a session ── */
    submitBids: async (_sessionId, bidId, onSuccess, onError) => {
      set({ submitBidStatus: "BEGIN" });

      const body = {
        bid_ids: bidId ? [parseInt(bidId)] : [],
      };

      api
        .post(BIDDING_HOST, APIendpoints.submitBids, body)
        .then(({ data }) => {
          set({ submitBidStatus: "DONE" });
          onSuccess && onSuccess(data);
        })
        .catch((err) => {
          set({ submitBidStatus: "FAIL" });
          onError && onError(_get(err, "response.data.error.message", "Failed to submit bid"));
        });
    },

    /* ── Get allotment for awarded bids ── */
    getAllotment: async (sessionId) => {
      const vendorId = window.localStorage.getItem(LS_KEYS.OUTLET_ID);

      return api
        .post(BIDDING_HOST, APIendpoints.getAllotment, {
          bidding_session_ids: [parseInt(sessionId)],
          ...(vendorId ? { participant_ids: [vendorId], participant_type: "VENDOR" } : {}),
        })
        .then(({ data }) => _get(data, "response.participant_allotments", []))
        .catch(() => []);
    },
  }))
);

export default useBiddingStore;
